import React, { useCallback, useEffect, useState } from 'react';
import { useModal } from 'react-modal-hook';
import { useDispatch, useSelector } from 'react-redux';
import debounce from 'lodash/debounce';
import { ModalFactory } from 'components/Modal';
import { DEFAULT_ORG_ADMIN_JOB_TITLE } from 'config/const';
import SelectUser from 'modals/SelectUser';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { selectCurrentEmployee } from 'store/App';
import { Employees, getEmployeesRequest, selectEmployees } from 'store/Collections';
import { IStore } from 'store/types';
import { AffiliationType, ApiQueryOperator, EmployeeType, IEmployee } from 'types';
import { formatJobTitle } from 'utils/jobs';

interface IProps {
  pageSize?: number;
  isEmployeeDisabled?(employee: IEmployee): [boolean, string];
  onEmployeeSelect(employee: IEmployee): void;
  onIsLoading?(isLoading: boolean): void;
}

export const useSelectSiteEmployee = ({ pageSize = 4, isEmployeeDisabled, onEmployeeSelect, onIsLoading }: IProps) => {
  const dispatch = useDispatch();

  const foundEmployees = useSelector(selectEmployees());
  const isLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Employees.GET_EMPLOYEES_REQUEST));
  const currentOrganizationId = useSelector(selectCurrentEmployee())?.organizationId;
  const currentSiteId = useSelector(selectCurrentEmployee())?.siteId || '';

  const [limit, setLimit] = useState(pageSize);
  const [fetchForgottenPayees, setFetchForgottenPayees] = useState(false);
  const [searchName, setSearchName] = useState('');

  useEffect(() => {
    onIsLoading?.(isLoading);
  }, [isLoading]);

  const filteredEmployees: Array<{
    id: string;
    username: string;
    image?: string;
    role?: string;
    site?: string;
    disabled?: boolean;
    hasPaymentMethod?: boolean;
  }> = foundEmployees?.data?.map((employee) => {
    const { sites } = employee;
    const site = sites?.find((site) => site.id === currentSiteId);
    const [disabled, disabledReason] = isEmployeeDisabled?.(employee) || [false, ''];

    return {
      id: employee.id,
      username: employee.name,
      image: employee.user?.avatarUrl,
      hasPaymentMethod: !!employee.user?.card,
      role: formatJobTitle(employee.type === EmployeeType.ADMIN ? DEFAULT_ORG_ADMIN_JOB_TITLE : site?.role || ''),
      site: site?.name || '',
      disabled,
      disabledReason: disabled ? disabledReason : '',
    };
  });

  useEffect(() => {
    if (fetchForgottenPayees && currentOrganizationId && currentSiteId) {
      dispatch(
        getEmployeesRequest(AffiliationType.SITES, currentSiteId, {
          limit: limit + pageSize + 1,
          sort: 'name',
          q: [
            ...(searchName
              ? [
                  {
                    key: 'name',
                    operator: ApiQueryOperator.like,
                    value: searchName,
                  },
                ]
              : []),
          ],
        })
      );
    }
  }, [fetchForgottenPayees, searchName, limit, currentSiteId]);

  const loadMore = useCallback(() => {
    setLimit(limit + pageSize);
  }, [limit, pageSize]);

  const onSelect = useCallback(
    (hideModal: () => void) => (id: string) => {
      const selectedEmployee = foundEmployees?.data?.find((employee) => employee.id === id);
      onEmployeeSelect(selectedEmployee);
      isEmployeeDisabled?.(selectedEmployee);
      setSearchName(null);
      hideModal();
    },
    [foundEmployees, onEmployeeSelect]
  );

  const [showAddEmployeeModal, hideAddEmployeeModal] = useModal(() => {
    const debounceMs = 500;
    const delayedHandleChange = debounce(setSearchName, debounceMs);

    setFetchForgottenPayees(true);

    return (
      <ModalFactory
        customModal
        onClose={() => {
          // setFetchForgottenPayees(false);
          hideAddEmployeeModal();
        }}
        isActive
      >
        <SelectUser
          title="Add Employee"
          users={filteredEmployees}
          isActive
          isLoading={isLoading}
          onSelect={onSelect(hideAddEmployeeModal)}
          onNameChange={(changedName: string) => {
            setLimit(pageSize);
            delayedHandleChange(changedName.trim());
          }}
          loadMore={loadMore}
          onClose={() => {
            setLimit(pageSize);
            setSearchName('');
            setFetchForgottenPayees(false);
            hideAddEmployeeModal();
          }}
          limit={limit}
        />
      </ModalFactory>
    );
  }, [filteredEmployees, isLoading, onSelect, loadMore, limit]);

  return [showAddEmployeeModal, hideAddEmployeeModal];
};
